import { caseApiRequest, dataPayloadErrorMessage, isDataPayloadSuccess } from './apiClient';
import type { ApiResult } from './types';
import { isCaseRequestUuid } from '../pages/task-case/taskCaseUpdateHelpers';

export type ValidateSubmitWorkflowTaskResult = {
  message: string;
  canSubmit: boolean;
  requiresClosureAllocation: boolean;
  nextStageIsClosure: boolean;
  closureUserTasksCreated: boolean;
  nextWorkflowTemplateStageId: string;
  nextWorkflowInstanceStageId: string;
  nextStageName: string;
};

function strField(row: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = row[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function boolField(row: Record<string, unknown>, ...keys: string[]): boolean {
  for (const k of keys) {
    const v = row[k];
    if (v === true || v === false) return v;
    if (v === 'true') return true;
    if (v === 'false') return false;
  }
  return false;
}

function normalizeValidateSubmitPayload(raw: unknown): ValidateSubmitWorkflowTaskResult | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  return {
    message: strField(o, 'message'),
    canSubmit: boolField(o, 'canSubmit', 'can_submit'),
    requiresClosureAllocation: boolField(o, 'requiresClosureAllocation', 'requires_closure_allocation'),
    nextStageIsClosure: boolField(o, 'nextStageIsClosure', 'next_stage_is_closure'),
    closureUserTasksCreated: boolField(o, 'closureUserTasksCreated', 'closure_user_tasks_created'),
    nextWorkflowTemplateStageId: strField(o, 'nextWorkflowTemplateStageId', 'next_workflow_template_stage_id'),
    nextWorkflowInstanceStageId: strField(o, 'nextWorkflowInstanceStageId', 'next_workflow_instance_stage_id'),
    nextStageName: strField(o, 'nextStageName', 'next_stage_name'),
  };
}

/**
 * CMS-CCA-6
 * POST /v1/case-requests/category-allocations/users/validate-submit-task
 *
 * Validate whether closure stage user tasks are allocated before submitting the current workflow task.
 */
export async function validateSubmitWorkflowTask(input: {
  caseRequestId: string;
  workflowInstanceStageUserTaskId: string;
  applicationCode?: string;
}): Promise<ApiResult<ValidateSubmitWorkflowTaskResult>> {
  const caseRequestId = input.caseRequestId.trim();
  const workflowInstanceStageUserTaskId = input.workflowInstanceStageUserTaskId.trim();

  if (!isCaseRequestUuid(caseRequestId)) return { success: false, message: 'Invalid case request id.' };
  if (!isCaseRequestUuid(workflowInstanceStageUserTaskId)) return { success: false, message: 'Invalid user task id.' };

  const applicationCode = (
    input.applicationCode ?? import.meta.env.VITE_APP_CODE_VALIDATE_SUBMIT_TASK ?? 'INTRANET'
  ).trim();
  if (!applicationCode) return { success: false, message: 'Missing application code.' };

  const res = await caseApiRequest<unknown>('/category-allocations/users/validate-submit-task', {
    method: 'POST',
    headers: { applicationCode, 'application-code': applicationCode },
    body: {
      case_request_id: caseRequestId,
      workflow_instance_stage_user_task_id: workflowInstanceStageUserTaskId,
    },
  });

  if (!res.ok) return { success: false, message: res.message };

  const data = normalizeValidateSubmitPayload(res.data);
  if (!data) return { success: false, message: 'Invalid validation response.' };

  if (!data.canSubmit) {
    return {
      success: false,
      message: data.message || 'Task cannot be submitted. Closure stage allocation may be required.',
    };
  }

  return { success: true, data };
}

/**
 * CMS-CCA-5
 * POST /v1/case-requests/category-allocations/users/submit-task
 *
 * Submit a REGULAR workflow task: mark it completed, optionally create a physical file movement record,
 * and advance the workflow instance to the next stage.
 */
export async function submitRegularWorkflowTask(input: {
  caseRequestId: string;
  workflowInstanceStageUserTaskId: string;
  physicalFileMovementRequired: boolean;
  documentMoverId?: string;
  applicationCode?: string;
}): Promise<ApiResult<void>> {
  const caseRequestId = input.caseRequestId.trim();
  const workflowInstanceStageUserTaskId = input.workflowInstanceStageUserTaskId.trim();
  const documentMoverId = (input.documentMoverId ?? '').trim();

  if (!isCaseRequestUuid(caseRequestId)) return { success: false, message: 'Invalid case request id.' };
  if (!isCaseRequestUuid(workflowInstanceStageUserTaskId)) return { success: false, message: 'Invalid user task id.' };
  if (input.physicalFileMovementRequired && !documentMoverId) {
    return { success: false, message: 'Document mover is required.' };
  }

  const applicationCode = (input.applicationCode ?? import.meta.env.VITE_APP_CODE_SUBMIT_TASK ?? 'INTRANET').trim();
  if (!applicationCode) return { success: false, message: 'Missing application code.' };

  const res = await caseApiRequest<{ status?: string; message?: string } | unknown>(
    '/category-allocations/users/submit-task',
    {
      method: 'POST',
      headers: { applicationCode, 'application-code': applicationCode },
      body: {
        case_request_id: caseRequestId,
        physical_file_movement_required: input.physicalFileMovementRequired,
        document_mover_id: input.physicalFileMovementRequired ? documentMoverId : null,
        workflow_instance_stage_user_task_id: workflowInstanceStageUserTaskId,
      },
    },
  );

  if (!res.ok) return { success: false, message: res.message };
  if (!isDataPayloadSuccess(res.data)) return { success: false, message: dataPayloadErrorMessage(res.data) };
  return { success: true, data: undefined };
}

/**
 * CMS-CRC-27
 * POST /v1/case-requests/complete-task
 *
 * Complete the current task and move the workflow to the next stage.
 */
export async function completeWorkflowTask(input: {
  caseRequestId: string;
  workflowInstanceStageUserTaskId: string;
  physicalFileMovementRequired: boolean;
  documentMoverId?: string;
  completionRemarks?: string;
  applicationCode?: string;
}): Promise<ApiResult<void>> {
  const caseRequestId = input.caseRequestId.trim();
  const workflowInstanceStageUserTaskId = input.workflowInstanceStageUserTaskId.trim();
  const documentMoverId = (input.documentMoverId ?? '').trim();

  if (!isCaseRequestUuid(caseRequestId)) return { success: false, message: 'Invalid case request id.' };
  if (!isCaseRequestUuid(workflowInstanceStageUserTaskId)) return { success: false, message: 'Invalid user task id.' };
  if (input.physicalFileMovementRequired && !documentMoverId) {
    return { success: false, message: 'Document mover is required.' };
  }

  const applicationCode = (input.applicationCode ?? import.meta.env.VITE_APP_CODE_COMPLETE_TASK ?? 'INTRANET').trim();
  if (!applicationCode) return { success: false, message: 'Missing application code.' };

  const res = await caseApiRequest<{ status?: string; message?: string } | unknown>('/complete-task', {
    method: 'POST',
    headers: { applicationCode, 'application-code': applicationCode },
    body: {
      case_request_id: caseRequestId,
      physical_file_movement_required: input.physicalFileMovementRequired,
      document_mover_id: input.physicalFileMovementRequired ? documentMoverId : null,
      workflow_instance_stage_user_task_id: workflowInstanceStageUserTaskId,
      ...(input.completionRemarks?.trim()
        ? { completion_remarks: input.completionRemarks.trim() }
        : {}),
    },
  });

  if (!res.ok) return { success: false, message: res.message };
  if (!isDataPayloadSuccess(res.data)) return { success: false, message: dataPayloadErrorMessage(res.data) };
  return { success: true, data: undefined };
}

